/**
 * "A newer build is deployed" detection (plan eager-prancing-music W0 §6).
 *
 * Builder tabs stay open for days. When a wave changes what the builders
 * write, an old tab keeps writing the old way until someone reloads it.
 * Every build carries its own id (`__BUILD_ID__`, injected by vite.config.ts)
 * and publishes the same id as `/version.json`. A running tab polls that file
 * and offers a reload when the ids differ — it never reloads on its own,
 * because a builder may hold unsaved work.
 *
 * `/version.json` is served `Cache-Control: no-store` (public/_headers). A
 * deploy without the file (or a rollback to before this feature) answers with
 * the SPA fallback — `200 text/html` — so anything that is not a JSON object
 * with a string `id` counts as "unknown", never as "new version".
 */

/** The id baked into the running bundle; "dev" outside production builds. */
export const CURRENT_BUILD_ID: string =
  typeof __BUILD_ID__ === "string" && __BUILD_ID__ ? __BUILD_ID__ : "dev";

/** Whether this bundle can take part in the check at all. */
export const VERSION_CHECK_ENABLED =
  import.meta.env.PROD && CURRENT_BUILD_ID !== "dev" && CURRENT_BUILD_ID !== "test";

/** The deployed build id, or null when it cannot be determined. */
export async function fetchDeployedBuildId(
  fetchImpl: typeof fetch = fetch,
  now: () => number = Date.now,
): Promise<string | null> {
  try {
    const res = await fetchImpl(`/version.json?t=${now()}`, {
      cache: "no-store",
      credentials: "omit",
    });
    if (!res.ok) return null;
    const type = res.headers.get("content-type") ?? "";
    if (!type.includes("application/json")) return null;
    const body: unknown = await res.json();
    if (typeof body !== "object" || body === null) return null;
    const id = (body as { id?: unknown }).id;
    return typeof id === "string" && id.length > 0 ? id : null;
  } catch {
    return null;
  }
}

/** True only when the deployed id is known AND differs from this bundle's. */
export function isNewerBuild(deployed: string | null, current: string = CURRENT_BUILD_ID): boolean {
  return deployed !== null && deployed !== current;
}
